import { FC, useMemo, useEffect, useCallback } from 'react';
import cn from 'classnames';
import { UiLoader } from '@frontend/znanie-uikit';

import { useCheckAnswer } from 'hooks/gamification/useCheckAnswer';
import { useDailyQuestion } from 'hooks/gamification/useDailyQuestion';
import { useDailyQuestionUserScore } from 'hooks/gamification/useDailyQuestionUserScore';

import { trackAnalyticsData, AnalyticsEvent } from 'utils/analytics';

import LayoutConfines from 'components/layout/LayoutConfines';

import { IconBlock } from './components/IconBlock';
import { ResultBlock } from './components/ResultBlock';
import { WaitingBlock } from './components/WaitingBlock';
import { QuestionBlock } from './components/QuestionBlock';

import { T as QuestionBlockT } from './components/QuestionBlock';

import BackgroundMobile from './images/background-mobile.svg';
import BackgroundDesktop from './images/background-desktop.svg';

import * as H from './DailyQuestion.hooks';
import * as C from './DailyQuestion.const';
import * as T from './DailyQuestion.types';

import css from './DailyQuestion.module.scss';

export const DailyQuestion: FC<T.IDailyQuestion> = (props) => {
  const {
    checkAnswer,
    data: answerData,
    error: answerError,
  } = useCheckAnswer();

  const { data: userScoreData, refetch: refetchUserScore } =
    useDailyQuestionUserScore(C.DAILY_COMPETITION_SLUG);

  const {
    data: questionData,
    error: questionError,
    loading: questionLoading,
    refetch: refetchQuestion,
  } = useDailyQuestion(C.DAILY_COMPETITION_SLUG);

  const { stage, isDisappearing, switchStage } = H.useSwitchStage();

  const answer = answerData?.checkAnswer?.checkAnswer;
  const question = questionData?.actualQuestion;
  const userScore = userScoreData?.dailyQuestionUserScore;

  const answerClickHandler = async (answer: QuestionBlockT.TAnswer) => {
    try {
      const checkAnswerParams = {
        answerId: Number(answer?.id),
        questionId: Number(question?.id),
      };

      await checkAnswer(checkAnswerParams);

      trackAnalyticsData(
        AnalyticsEvent.DAILY_QUESTION_SEND_ANSWER,
        checkAnswerParams
      );

      switchStage(C.STAGES.RESULTS);
    } catch (err) {
      console.error(err);
    }
  };

  const closeResultsHandler = useCallback(() => {
    switchStage(C.STAGES.WAITING);

    trackAnalyticsData(AnalyticsEvent.DAILY_QUESTION_CLOSE_RESULT);
  }, []);

  const blockClassName = useMemo(
    () => cn(css.contentBlock, isDisappearing && css.disappearing),
    [isDisappearing]
  );

  const refetchData = async () => {
    try {
      await refetchQuestion();
    } catch (err) {
      console.error(err);
    }

    try {
      await refetchUserScore();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!questionLoading && stage === C.STAGES.INITIAL) {
      if (question) {
        switchStage(C.STAGES.QUESTION);
      } else if (questionError?.message === C.USER_ANSWERED_QUESTION) {
        switchStage(C.STAGES.WAITING);
      }
    }
  }, [question, questionError, questionLoading]);

  useEffect(() => {
    if (answerError?.message === C.USER_ANSWERED_QUESTION) {
      switchStage(C.STAGES.WAITING);
    }
  }, [answerError]);

  useEffect(() => {
    if (answer) {
      trackAnalyticsData(AnalyticsEvent.DAILY_QUESTION_ANSWER_RESULT, {
        isRight: answer.isRight,
      });
    }
  }, [answer?.isRight]);

  if (
    !question &&
    !questionLoading &&
    questionError?.message !== C.USER_ANSWERED_QUESTION
  ) {
    return null;
  }

  return (
    <LayoutConfines>
      <div className={cn(css.container, props.isDarkTheme && css.darkTheme)}>
        <div className={css.block}>
          <IconBlock stage={stage} isAnswerRight={answer?.isRight!} />
        </div>

        <div className={css.block}>
          {!props.isDarkTheme && (
            <div className={css.backgroundWrapper}>
              <BackgroundDesktop
                width="100%"
                height="100%"
                color={C.DEFAULT_QUESTION_COLOR}
                className={css.desktopBackgroundImage}
              />
              <BackgroundMobile
                width="100%"
                height="100%"
                color={C.DEFAULT_QUESTION_COLOR}
                className={css.mobileBackgroundImage}
              />
            </div>
          )}

          <div className={cn(css.contentWrapper, css.resultsState)}>
            <UiLoader loading={stage === C.STAGES.INITIAL}>
              <>
                {stage === C.STAGES.QUESTION && (
                  <div className={blockClassName}>
                    <QuestionBlock
                      image={question?.image!}
                      answers={question?.answerTexts!}
                      question={question?.text!}
                      onAnswerClick={answerClickHandler}
                      userScore={userScore?.amount!}
                      multiplier={userScore?.multiplier!}
                      refetchData={refetchData}
                      isDarkTheme={props.isDarkTheme}
                      isCinemaWeek={props.isCinemaWeek}
                    />
                  </div>
                )}

                {stage === C.STAGES.RESULTS && (
                  <div className={blockClassName}>
                    <ResultBlock
                      image={question?.image!}
                      answer={answer?.textProof!}
                      question={question?.text!}
                      multiplier={userScore?.multiplier!}
                      isAnswerRight={answer?.isRight!}
                      onClose={closeResultsHandler}
                      isDarkTheme={props.isDarkTheme}
                    />
                  </div>
                )}

                {stage === C.STAGES.WAITING && (
                  <div className={blockClassName}>
                    <WaitingBlock
                      userScore={userScore?.amount!}
                      multiplier={userScore?.multiplier!}
                      answersCount={userScore?.countOperations!}
                      onTimerEnd={refetchData}
                      isDarkTheme={props.isDarkTheme}
                      isCinemaWeek={props.isCinemaWeek}
                    />
                  </div>
                )}
              </>
            </UiLoader>
          </div>
        </div>
      </div>
    </LayoutConfines>
  );
};
